import type { EntityRootData } from '@web-config-app/core';
import { cloneDeep, isEmpty, isPlainObject } from 'lodash-es';

/**
 * Infer a placeholder for a single default array element so the locale array can be padded.
 * Supported element types (we consider these "localizable"):
 *  - string => '' (keeps index aligned, signals untranslated)
 *  - plain object => {} (retains structural container so nested arrays can also be visited)
 * Unsupported types (number, boolean, array, null, undefined) => return undefined (caller skips padding for arrays containing any of these)
 */
function inferArrayItemPlaceholder(arrayItem: any): any {
  if (typeof arrayItem === 'string') return '';
  if (arrayItem && typeof arrayItem === 'object' && !Array.isArray(arrayItem))
    return {};
  return undefined; // signal unsupported
}

function isEmptyObject(value: unknown): boolean {
  return isPlainObject(value) && isEmpty(value);
}

/**
 * Recursively walk the default locale data alongside a specific locale's data and pad arrays.
 * Key rules:
 *  - Only pad when every element in the default array is a supported (localizable) type.
 *  Truncates locale arrays to match default length, removing extra elements only if extra elements are an empty object.
 *  - Never create missing arrays or object properties in the locale (non-destructive; consumer may intentionally omit).
 *  - Empty default arrays do not clear non-empty locale arrays.
 *  - Recursion only descends into plain objects (strings are terminals) to reach nested arrays.
 */
function padLocaleArrays(
  defaultLocaleData: any,
  nonDefaultLocaleData: any,
  // WeakSet: cycle guard without retaining strong references
  visitedDefaultLocaleProperties = new WeakSet(),
): void {
  if (defaultLocaleData && typeof defaultLocaleData === 'object') {
    if (visitedDefaultLocaleProperties.has(defaultLocaleData)) return;
    visitedDefaultLocaleProperties.add(defaultLocaleData);
  }

  // ARRAY HANDLING
  if (Array.isArray(defaultLocaleData)) {
    if (!Array.isArray(nonDefaultLocaleData)) return;

    // Determine if this array is fully localizable. If ANY element is an unsupported type
    // we skip padding entirely for this array to avoid pushing meaningless placeholders (e.g., for numbers).
    const allArrayItemsDataSupported = defaultLocaleData.every(
      (data: any) =>
        typeof data === 'string' ||
        (data && typeof data === 'object' && !Array.isArray(data)),
    );

    /** pad when:
     * - allArrayItemsDataSupported -> every default element is a string or plain object (localizable)
     * - defaultLocaleData.length > nonDefaultLocaleData.length
     * - defaultLocaleData.length > 0 (we never clear a non-empty locale array when default becomes empty)
     */
    if (
      allArrayItemsDataSupported &&
      defaultLocaleData.length > nonDefaultLocaleData.length &&
      defaultLocaleData.length > 0
    ) {
      defaultLocaleData
        .slice(nonDefaultLocaleData.length)
        .forEach((defaultLocaleElement: any) => {
          /**
           * padding logic: append inferred placeholder for each missing default element index.
           * - default string  -> ''
           * - default object  -> {}
           * (We only reach here if every default element is string or plain object.)
           */
          nonDefaultLocaleData.push(
            inferArrayItemPlaceholder(defaultLocaleElement),
          );
        });
    }

    /**
     * Non-default locale array TRIMMING LOGIC: remove trailing empty objects when non-default locale array is longer than the corresponding default-locale array.
     * This handles cases where the non-default locale has extra empty objects (caused by a previously fixed issue) that should be removed.
     */
    if (
      allArrayItemsDataSupported &&
      nonDefaultLocaleData.length > defaultLocaleData.length
    ) {
      const lastNonEmptyIndex = nonDefaultLocaleData.findLastIndex(
        (item: any) =>
          !(
            item &&
            typeof item === 'object' &&
            !Array.isArray(item) &&
            isEmptyObject(item)
          ),
      );

      /**
       * this trimming logic follows two rules:
       * 1. Never trim below the default array length (preserve required padding)
       * 2. Never remove non-empty data that exists beyond the default length
       */
      const newLength = Math.max(
        defaultLocaleData.length,
        lastNonEmptyIndex + 1,
      );
      nonDefaultLocaleData.splice(newLength);
    }

    /**
     * for each array item index: if BOTH defaultLocaleData + nonDefaultLocaleData elements are plain objects, recurse into them.
     * We do NOT recurse when an element is:
     *   - a primitive (string/number/boolean/null/undefined) -> nothing deeper to inspect
     *   - an array -> arrays-of-arrays are intentionally ignored
     * This is enough to reach nested arrays because they live under object properties.
     */
    defaultLocaleData.forEach((defaultLocaleItemData: any, index: number) => {
      const localeElement = nonDefaultLocaleData[index];
      if (
        defaultLocaleItemData &&
        typeof defaultLocaleItemData === 'object' &&
        !Array.isArray(defaultLocaleItemData) &&
        localeElement &&
        typeof localeElement === 'object' &&
        !Array.isArray(localeElement)
      ) {
        padLocaleArrays(
          defaultLocaleItemData,
          localeElement,
          visitedDefaultLocaleProperties,
        );
      }
    });
    return;
  }

  // OBJECT HANDLING
  if (
    defaultLocaleData &&
    typeof defaultLocaleData === 'object' &&
    !Array.isArray(defaultLocaleData) &&
    nonDefaultLocaleData &&
    typeof nonDefaultLocaleData === 'object' &&
    !Array.isArray(nonDefaultLocaleData)
  ) {
    Object.keys(defaultLocaleData).forEach((property) => {
      // do not create missing properties in non-default locale - we only pad existing arrays
      if (!(property in nonDefaultLocaleData)) return;
      padLocaleArrays(
        defaultLocaleData[property],
        nonDefaultLocaleData[property],
        visitedDefaultLocaleProperties,
      );
    });
  }
}

/**
 * Syncs (pads) array lengths across locale data based on the default locale's array lengths.
 *
 * Behavior:
 * - Shorter locale arrays are padded with placeholders ('' or {}) to match the default locale length.
 * - Longer locale arrays are preserved (we never remove author-added elements).
 * - Missing arrays / object branches are NOT created in the locale.
 * - Empty default arrays do not clear locale arrays.
 * - Only arrays whose default elements are ALL strings or plain objects are padded; arrays containing any unsupported type are skipped entirely.
 */
/**
 * @param entityData Entity data to sync
 */

export const syncLocalizedArrayLengths = (
  entityData: EntityRootData,
): EntityRootData => {
  // Clone so caller's data is not mutated.
  const clonedEntity = cloneDeep(entityData) as EntityRootData;
  const translationsByLocale = clonedEntity.attributes?.translations;
  const defaultDomainAttributes =
    clonedEntity.attributes?.domainEntityAttributes;

  // if translations do not exist or default attributes do not exist, just return the data
  if (!translationsByLocale || !defaultDomainAttributes) return entityData;

  Object.keys(translationsByLocale).forEach((localeCode) => {
    const localeTranslationEntry = translationsByLocale[localeCode];
    const localeDomainAttributes =
      localeTranslationEntry?.domainEntityAttributes;
    // if locale domain attributes do not exist, skip
    if (!localeDomainAttributes) return;
    try {
      padLocaleArrays(
        defaultDomainAttributes,
        localeDomainAttributes,
        new WeakSet(),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Error syncing arrays for locale ${localeCode}:`, err);
    }
  });

  return clonedEntity;
};
